import { ApolloClient, useApolloClient } from '@apollo/client';
import { Checkbox, Input } from '@arcadiapower/shrike';
import { useMachine } from '@xstate/react';
import { ReactElement, ComponentProps, HTMLInputTypeAttribute } from 'react';
import { SpaceProps } from 'styled-system';
import { Container } from '~/components/layout';
import { IS_TEST } from '~/utils/constants';
import { getStyledSystemProps, removeStyledSystemProps } from '~/utils/styled-system';
import { validatorMachine } from './validating-input.machine';
import { Requirements, StyledInput } from './validating-input.style';

type ShrikeInputTypeAttribute = Extract<
  HTMLInputTypeAttribute,
  'password' | 'number' | 'text' | 'tel'
>;
export interface ValidatingInputProps extends InputProps, SpaceProps {
  debounceMessage?: number;
  debounceValidation?: number;
  fieldName?: string;
  onChange: OnChange;
  optional?: boolean;
  requirements?: string;
  validate: Validator;
  type?: HTMLInputTypeAttribute;
  value?: string | boolean;
}

export const ValidatingInput = ({
  id,
  debounceMessage = IS_TEST ? 1 : 750,
  debounceValidation = IS_TEST ? 1 : 250,
  fieldName,
  label,
  name,
  onChange,
  optional,
  requirements,
  validate,
  value,
  type,
  children,
  ...props
}: ValidatingInputProps) => {
  const apollo = useApolloClient();
  const getFieldDefaultValue = () => {
    switch (type) {
      case 'checkbox':
        return false;
      default:
        return '';
    }
  };

  const [{ context, matches }, send] = useMachine(validatorMachine, {
    context: {
      apollo,
      fieldName: fieldName || name,
      value: value || getFieldDefaultValue(),
      onChange,
      optional,
      validate,
    },
    delays: { debounceMessage, debounceValidation },
  });

  switch (type) {
    case 'checkbox':
      return (
        <Container {...getStyledSystemProps(props)}>
          <Checkbox
            id={id}
            name={name}
            margin="16px 0"
            data-testid="validating-input-checkbox"
            checked={!!context.value}
            onChange={e => send({ type: 'CHANGE', value: e.target.checked })}
          >
            {children}
          </Checkbox>
        </Container>
      );
    default: {
      return (
        <Container {...getStyledSystemProps(props)}>
          <StyledInput
            {...removeStyledSystemProps(props)}
            errorText={matches('invalid.showing') ? context.message : ''}
            label={label}
            name={name}
            onBlur={() => send({ type: 'BLUR' })}
            onFocus={() => send({ type: 'FOCUS' })}
            onChange={value => send({ type: 'CHANGE', value })}
            value={context.value as string}
            type={type as ShrikeInputTypeAttribute}
          />
          {requirements && (
            <Requirements
              className={matches('valid') ? 'valid' : 'invalid'}
              isValid={matches('valid')}
            >
              {requirements}
            </Requirements>
          )}
        </Container>
      );
    }
  }
};

export interface InputComponentProps
  extends SpaceProps,
    Omit<ValidatingInputProps, 'name' | 'label' | 'placeholder' | 'validate'> {}

export type OnChangeMeta = { name: string; isValid: boolean; optional?: boolean };

type InputProps = Omit<ComponentProps<typeof Input>, 'onChange' | 'margin' | 'type' | 'value'>;

type ValidationResponse = boolean | string | ReactElement;

type Validator = (options: {
  apollo?: ApolloClient<{}>;
  value: string | boolean;
}) => ValidationResponse | Promise<ValidationResponse>;

export type OnChange<TValue extends any = string> = (value: TValue, params: OnChangeMeta) => void;
